import { useEffect, useState } from "react";
import { Award, Brain, CheckCircle2, Trophy } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { useStudyProgressState } from "../../lib/useStudyProgress";
import { ProgressRing } from "../ui/ProgressRing";
import { Skeleton } from "../ui/Skeleton";

type Activity = { quizzesCompleted: number; quizzesStarted: number; examsCompleted: number; cocCompleted: number };

function Cell({ icon, label, value, note }: { icon: React.ReactNode; label: string; value: number; note?: string }) {
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-tile bg-soft px-3 py-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-primary" aria-hidden="true">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-xs font-medium text-muted-foreground">{label}</span>
        <span className="block font-display text-xl font-bold leading-tight text-foreground">{value}</span>
        {note && <span className="block truncate text-xs text-muted-foreground">{note}</span>}
      </span>
    </div>
  );
}

/** Detailed study stats. They live here (and as a short summary on the dashboard), not on every page. */
export function ProfileStats() {
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? "";
  const { data: progress, loading: progressLoading } = useStudyProgressState();
  const [activity, setActivity] = useState<Activity | null | undefined>(undefined);

  useEffect(() => {
    if (!token) return;
    api.student
      .dashboard(token)
      .then((d) => setActivity(d.activityStats ?? null))
      .catch(() => setActivity(null));
  }, [token]);

  const courses = progress?.courses ?? [];
  const total = courses.reduce((n, c) => n + c.total, 0);
  const done = courses.reduce((n, c) => n + c.done, 0);
  const pct = total > 0 ? (done / total) * 100 : 0;

  if (progressLoading || activity === undefined) {
    return (
      <section aria-busy="true" aria-label="Loading your progress" className="space-y-3">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-64 w-full rounded-card" />
      </section>
    );
  }

  return (
    <section aria-labelledby="profile-progress" className="space-y-3">
      <h2 id="profile-progress" className="text-lg font-bold font-display">Your progress</h2>
      <div className="space-y-4 rounded-card border border-border bg-card p-4 shadow-soft sm:p-5">
        <div className="flex items-center gap-4">
          <ProgressRing value={pct} size={64} stroke={6} label="Chapters finished" />
          <div className="min-w-0">
            <p className="font-display text-2xl font-bold text-foreground">{total > 0 ? `${done}/${total}` : "0"}</p>
            <p className="text-sm text-muted-foreground">chapters finished</p>
          </div>
        </div>

        {activity && (
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            <Cell
              icon={<Brain size={18} />}
              label="Quizzes"
              value={activity.quizzesCompleted}
              note={activity.quizzesStarted > activity.quizzesCompleted ? `${activity.quizzesStarted} started` : undefined}
            />
            <Cell icon={<Trophy size={18} />} label="Past exams" value={activity.examsCompleted} />
            <Cell icon={<Award size={18} />} label="COC practice" value={activity.cocCompleted} />
          </div>
        )}

        {courses.length > 0 && (
          <ul className="space-y-3 border-t border-border pt-4">
            {courses.map((c) => {
              const p = c.total > 0 ? Math.round((c.done / c.total) * 100) : 0;
              return (
                <li key={c.course_id} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="flex min-w-0 items-center gap-1.5 font-medium text-foreground">
                      {c.total > 0 && c.done >= c.total && <CheckCircle2 size={14} className="shrink-0 text-success" aria-label="Finished" />}
                      <span className="truncate">{c.name}</span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {c.done}/{c.total}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={p} aria-label={`${c.name} progress`}>
                    <div className="h-full rounded-full bg-primary" style={{ width: `${p}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
